import React, { useEffect, useState } from "react";
import { Award, Crown, Mic, Star, BookOpen, Sparkles, ExternalLink, FileText, X } from "lucide-react";

// --- Uploaded media artifacts (self-hosted for reliability) ---
const IMG = {
    sistersConf: "/assets/press/5zw64h0c.jpg",
    room2Breathe: "/assets/press/s2q7kwqb.jpg",
    // "Embrace Your Cake" is the specific anthology Lisa/Melissa contributed to.
    embraceYourCake: "/assets/press/4xpm96d0.jpg",
    // Actual dated magazine covers
    phenomenalDec2021: "/assets/press/vqcgxltw.jpg",
    hoinser2022: "/assets/press/no1m23wm.jpg",
    // Heidens April 2023 uses the same publisher's collection cover (Hoinser Queens Book 2023)
    heidens2023: "/assets/press/pr21r64n.jpg",
    // Presidential Lifetime Achievement Award — official certificate + letter
    presidentialCert: "/assets/press/wytp37nk.jpg",
    presidentialLetter: "/assets/press/g8j1819c.jpg",
    // Fellowship For Christ Ministries — Evangelist Training Conference flyer
    fellowshipConf: "/assets/press/f7uc1uvl.jpg",
    // Influential Women for Inspiration Impact — recognition graphic
    influentialWomen: "/assets/press/2ieqfnjg.jpg",
};

// --- Publications / feature grid ---
const publications = [
    {
        id: "phenomenal",
        title: "Phenomenal Overcomer Magazine",
        subtitle: "Feature — “Set Free from the Shackles of Bondage”",
        year: "December 2021",
        thumbnail: IMG.phenomenalDec2021,
        publisher: "LL Media Group",
    },
    {
        id: "hoinser-top100",
        title: "Hoinser Queens Book",
        subtitle: "Named — Top 100 Inspirational Women",
        year: "2022",
        thumbnail: IMG.hoinser2022,
        publisher: "Hoinser & Heidens",
    },
    {
        id: "heidens",
        title: "Heidens Magazine",
        subtitle: "Feature — “Your Brand · Powerful Impact”",
        year: "April 2023",
        thumbnail: IMG.heidens2023,
        publisher: "Hoinser & Heidens",
    },
    {
        id: "embrace-your-cake",
        title: "Embrace Your Cake — Anthology",
        subtitle: "Contributing author · Sisters Inspire Sisters Network",
        year: "Featured",
        thumbnail: IMG.embraceYourCake,
        publisher: "Sisters Inspire Sisters",
    },
    {
        id: "room2breathe",
        title: "Room2Breathe Podcast",
        subtitle: "Featured guest interview",
        year: "Guest appearance",
        thumbnail: IMG.room2Breathe,
        publisher: "Room2Breathe",
    },
    {
        id: "sisters-conf",
        title: "Sisters Inspire Sister Speaking Conference",
        subtitle: "Invited speaker · Charlotte, NC",
        year: "April 2022",
        thumbnail: IMG.sistersConf,
        publisher: "Sisters Inspire Sisters",
    },
    {
        id: "fellowship-conf",
        title: "Evangelist Training Conference",
        subtitle: "Invited speaker on “Revival” · June 9–13",
        year: "Speaking engagement",
        thumbnail: IMG.fellowshipConf,
        publisher: "Fellowship For Christ Ministries",
    },
    {
        id: "influential-women",
        title: "Influential Women for Inspiration Impact",
        subtitle: "Recognized honoree",
        year: "Recognition",
        thumbnail: IMG.influentialWomen,
        publisher: "Influential Women",
    },
];

// --- Speaking + recognition list (currently all captured visually above) ---
const speaking = [];

/**
 * Recognition section — sits on the Press Kit page. Anchors with the
 * Presidential Lifetime Achievement Award, then walks through media features,
 * speaking engagements, and non-visual recognitions.
 */
export const Recognition = () => {
    // Lightbox state — holds the URL + label of the image being viewed inline.
    const [viewing, setViewing] = useState(null); // { src, title } | null

    // Close on ESC key
    useEffect(() => {
        if (!viewing) return;
        const onKey = (e) => e.key === "Escape" && setViewing(null);
        window.addEventListener("keydown", onKey);
        // Lock body scroll while the lightbox is open
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = originalOverflow;
        };
    }, [viewing]);

    const openViewer = (src, title) => setViewing({ src, title });

    return (
        <section
            id="recognition"
            data-testid="press-recognition"
            className="mx-auto max-w-6xl px-6 md:px-10 pb-14"
        >
            {/* Presidential award — top featured card */}
            <article
                data-testid="press-award-presidential"
                className="relative overflow-hidden rounded-[32px] p-8 md:p-12 bg-gradient-to-br from-lilac-900 via-[#3B2748] to-[#5D3E74] text-white border border-gold/50 shadow-[0_40px_80px_rgba(43,29,53,0.28)]"
            >
                <div
                    className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl bg-gold/25"
                    aria-hidden
                />
                <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:gap-10 items-start">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold text-white text-[10px] font-bold tracking-[0.2em] uppercase shadow-[0_10px_30px_rgba(212,175,55,0.5)]">
                            <Crown className="w-3.5 h-3.5" /> The highest honor
                        </div>

                        <div className="mt-6 grid grid-cols-1 md:grid-cols-[auto_1fr] items-start gap-6 md:gap-8">
                            <div className="shrink-0">
                                <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-gold text-white grid place-items-center shadow-[0_20px_50px_rgba(212,175,55,0.55)]">
                                    <Award className="w-12 h-12" strokeWidth={1.5} />
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.24em] text-gold-deep font-bold">
                                    2023 · Federal Recognition
                                </div>
                                <h2 className="mt-2 font-display text-3xl md:text-5xl leading-[1.05]">
                                    Presidential <em className="italic gold-text">Lifetime</em>
                                    <br />
                                    Achievement Award.
                                </h2>
                                <p className="mt-4 text-white/85 max-w-2xl leading-relaxed">
                                    Signed by <strong>President Joseph R. Biden, Jr.</strong> — awarded
                                    for a lifetime of service, impact, and pouring into others. It&rsquo;s the
                                    weight behind every episode Coach Melissa records.
                                </p>

                                <div className="mt-6 flex flex-wrap gap-3">
                                    <button
                                        onClick={() => openViewer(IMG.presidentialCert, "Presidential Lifetime Achievement Award — Certificate")}
                                        data-testid="press-award-cert-link"
                                        className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-gold hover:bg-gold-deep text-white font-semibold text-sm shadow-[0_12px_32px_rgba(212,175,55,0.4)] transition-all"
                                    >
                                        <Award className="w-4 h-4" /> View certificate
                                    </button>
                                    <button
                                        onClick={() => openViewer(IMG.presidentialLetter, "Presidential Lifetime Achievement Award — Letter")}
                                        data-testid="press-award-letter-link"
                                        className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold text-sm transition-all"
                                    >
                                        <FileText className="w-4 h-4" /> View letter
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Certificate thumbnail — click to view full-size */}
                    <button
                        type="button"
                        onClick={() => openViewer(IMG.presidentialCert, "Presidential Lifetime Achievement Award — Certificate")}
                        data-testid="press-award-cert-thumb"
                        className="group hidden lg:block relative w-52 xl:w-64 shrink-0 rounded-2xl overflow-hidden border-2 border-gold/50 shadow-[0_20px_50px_rgba(0,0,0,0.35)] hover:shadow-[0_30px_70px_rgba(212,175,55,0.35)] transition-all"
                        aria-label="View Presidential Lifetime Achievement Award certificate"
                    >
                        <img
                            src={IMG.presidentialCert}
                            alt="Presidential Lifetime Achievement Award certificate"
                            className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.03] select-none pointer-events-none"
                            loading="lazy"
                            draggable={false}
                        />
                        <span className="absolute bottom-3 left-3 right-3 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full bg-gold text-white text-[10px] font-bold tracking-[0.18em] uppercase">
                            Tap to view
                        </span>
                    </button>
                </div>
            </article>

            {/* Publications + features grid */}
            <div className="mt-14">
                <div className="flex items-end justify-between gap-6 mb-6">
                    <div>
                        <div className="eyebrow text-gold-deep flex items-center gap-2">
                            <BookOpen className="w-3.5 h-3.5" /> Media features
                        </div>
                        <h3 className="mt-3 font-display text-3xl md:text-4xl">
                            Featured <em className="italic gold-text">by</em>.
                        </h3>
                    </div>
                    <p className="hidden md:block text-sm text-lilac-700 italic max-w-xs">
                        The rooms that have already made space for her voice.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {publications.map((pub) => (
                        <article
                            key={pub.id}
                            data-testid={`press-feature-${pub.id}`}
                            className="group relative overflow-hidden rounded-3xl bg-white border border-lilac-200 hover:border-gold hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(43,29,53,0.14)] transition-all"
                        >
                            <div className="relative aspect-[4/5] overflow-hidden bg-lilac-100">
                                <img
                                    src={pub.thumbnail}
                                    alt={`${pub.title} — ${pub.subtitle}`}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                    loading="lazy"
                                />
                                <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/95 text-lilac-900 text-[9.5px] font-bold uppercase tracking-[0.18em]">
                                    <Star className="w-3 h-3 text-gold-deep" /> {pub.year}
                                </span>
                            </div>
                            <div className="px-5 py-4">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-gold-deep font-bold">
                                    {pub.publisher}
                                </div>
                                <div className="mt-2 font-display text-xl text-lilac-900 leading-tight">
                                    {pub.title}
                                </div>
                                <div className="mt-1.5 text-xs text-lilac-700 leading-relaxed">
                                    {pub.subtitle}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>

            {/* Booking CTA */}
            <div className="mt-14 rounded-[32px] bg-gradient-to-br from-[#FFF7E5] via-white to-[#FAF5FC] border border-gold/30 p-8 md:p-12 text-center">
                <div className="eyebrow text-gold-deep flex items-center justify-center gap-2">
                    <Mic className="w-3.5 h-3.5" /> Book Coach Melissa
                </div>
                <h3 className="mt-3 font-display text-3xl md:text-4xl">
                    Bring her voice to <em className="italic gold-text">your room</em>.
                </h3>
                <p className="mt-4 max-w-2xl mx-auto text-lilac-700 leading-relaxed">
                    For speaking engagements, podcast interviews, panels, ministry
                    events, or media features — reach out any time. She responds
                    personally.
                </p>
                <a
                    href="mailto:melissa@walkwithpurposeco.site?subject=Booking%20inquiry%20-%20Coach%20Melissa"
                    data-testid="press-book-cta"
                    className="mt-8 inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-lilac-900 hover:bg-lilac-700 text-white font-semibold text-sm shadow-[0_14px_36px_rgba(43,29,53,0.20)] transition-all"
                >
                    <ExternalLink className="w-4 h-4" />
                    melissa@walkwithpurposeco.site
                </a>
            </div>

            {/* Lightbox — inline viewer for the Presidential award artifacts.
                No download button, right-click disabled, blocks image drag. */}
            {viewing && (
                <div
                    data-testid="press-lightbox"
                    role="dialog"
                    aria-modal="true"
                    aria-label={viewing.title}
                    className="fixed inset-0 z-[120] grid place-items-center p-4 md:p-8 animate-lightbox-fade"
                    onClick={() => setViewing(null)}
                    onContextMenu={(e) => e.preventDefault()}
                >
                    <div className="absolute inset-0 bg-lilac-900/85 backdrop-blur-sm" aria-hidden />

                    <div
                        className="relative max-w-4xl w-full max-h-[92vh] flex flex-col gap-3"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between text-white">
                            <span className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-gold-deep font-bold">
                                {viewing.title}
                            </span>
                            <button
                                type="button"
                                onClick={() => setViewing(null)}
                                data-testid="press-lightbox-close"
                                aria-label="Close viewer"
                                className="grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-white transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div
                            className="relative flex-1 rounded-2xl overflow-hidden border-2 border-gold/50 shadow-[0_40px_80px_rgba(0,0,0,0.5)] bg-black/20"
                            onContextMenu={(e) => e.preventDefault()}
                        >
                            <img
                                src={viewing.src}
                                alt={viewing.title}
                                draggable={false}
                                className="w-full h-full max-h-[82vh] object-contain select-none"
                                onContextMenu={(e) => e.preventDefault()}
                                onDragStart={(e) => e.preventDefault()}
                                style={{ WebkitUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none" }}
                            />
                            {/* Invisible overlay stops long-press "save image" on most touch devices */}
                            <div
                                className="absolute inset-0 pointer-events-auto"
                                style={{ WebkitTouchCallout: "none" }}
                                onContextMenu={(e) => e.preventDefault()}
                                aria-hidden
                            />
                        </div>

                        <p className="text-center text-[11px] italic text-white/60">
                            View-only &middot; Please do not reproduce or distribute.
                        </p>
                    </div>

                    <style>{`
                        @keyframes lightboxFade {
                            from { opacity: 0; }
                            to   { opacity: 1; }
                        }
                        .animate-lightbox-fade { animation: lightboxFade 260ms ease-out both; }
                    `}</style>
                </div>
            )}
        </section>
    );
};
